import React, { useEffect, useMemo, useState } from "react";
import { Alert, Card, Form, Spinner, Table } from "react-bootstrap";
import { getInvoiceLembagaPelatihan } from "../../services/api";
import "./LpkDataPelatihanPenyeliaHalal.css";

const buildFileUrl = (fileName) => {
  if (!fileName) return "";
  return `https://lembaga.halal.go.id/invoice/${encodeURIComponent(fileName)}`;
};

const formatCurrency = (value) => {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "Rp 0";

  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const LpkInvoice = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await getInvoiceLembagaPelatihan();
        const data = Array.isArray(response.data?.data) ? response.data.data : [];
        setRows(data);
      } catch (err) {
        setRows([]);
        setError(
          err.response?.data?.message || "Gagal memuat data invoice lembaga pelatihan.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  const filteredRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((row) => {
      return (
        String(row?.no_inv || "").toLowerCase().includes(q) ||
        String(row?.jenis || "").toLowerCase().includes(q) ||
        String(row?.angkatan || "").toLowerCase().includes(q) ||
        String(row?.ref_desc || "").toLowerCase().includes(q)
      );
    });
  }, [rows, searchText]);

  return (
    <div className="lpk-penyelia-page">
      <Card className="lpk-main-card">
        <Card.Header className="lpk-card-header">Invoice LPK</Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Form.Group className="lpk-form-row lpk-search-input mb-3" controlId="searchInvoiceLpk">
            <Form.Label>Cari No Invoice / Jenis / Angkatan / Status</Form.Label>
            <Form.Control
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Contoh: 25000597"
            />
          </Form.Group>

          <div className="table-responsive mt-3">
            <Table bordered hover size="sm" className="mb-0 lpk-table">
              <thead>
                <tr>
                  <th>No</th>
                  <th>No Invoice</th>
                  <th>Tgl Invoice</th>
                  <th>Jatuh Tempo</th>
                  <th>Jenis</th>
                  <th>Angkatan</th>
                  <th>Jumlah</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Tgl Bayar</th>
                  <th>File Invoice</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={11}>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Memuat data...
                    </td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={11}>Tidak ada data invoice</td>
                  </tr>
                ) : (
                  filteredRows.map((row, idx) => (
                    <tr key={row?.id_inv || `${row?.no_inv || "row"}-${idx}`}>
                      <td>{idx + 1}</td>
                      <td>{row?.no_inv || "-"}</td>
                      <td>{formatDate(row?.tgl_inv)}</td>
                      <td>{formatDate(row?.duedate)}</td>
                      <td>{row?.jenis || "-"}</td>
                      <td>{row?.angkatan || "-"}</td>
                      <td>{row?.jumlah ?? "-"}</td>
                      <td>{formatCurrency(row?.total_inv)}</td>
                      <td>{row?.ref_desc || row?.status_payment || "-"}</td>
                      <td>{formatDate(row?.tgl_bayar)}</td>
                      <td>
                        {row?.file_inv ? (
                          <a
                            href={buildFileUrl(row.file_inv)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {row.file_inv}
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default LpkInvoice;
